import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { applySeo, buildUrl, getSiteUrl } from '@/lib/seo';
import {
  buildWebSiteSchema,
  buildOrganizationSchema,
  buildArticleSchema,
  buildBreadcrumbSchema,
  injectJsonLd,
  removeJsonLd,
} from '@/lib/schema';

const HOME_TITLE = 'AllChinaBuy Spreadsheet — Product Guides, Sellers & Resources 2026';
const HOME_DESCRIPTION =
  'Discover AllChinaBuy spreadsheet resources, product guides, seller information, QC tips, shipping costs and buying guides. Independent informational resource for AllChinaBuy buyers.';
const HOME_KEYWORDS =
  'AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy coupon, AllChinaBuy discount, buying agent guide, AllChinaBuy sellers, AllChinaBuy products, quality control, AllChinaBuy warehouse';
const ARTICLE_KEYWORDS =
  'AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy guide, AllChinaBuy shipping, AllChinaBuy buying guide, buying agent';

const HOME_PATHS = ['/', '/en', '/fr', '/de', '/da'];

function clearArticleJsonLd(): void {
  removeJsonLd('ld-website');
  removeJsonLd('ld-organization');
  removeJsonLd('ld-article');
  removeJsonLd('ld-breadcrumb');
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    if (HOME_PATHS.includes(path)) {
      const canonical = buildUrl(path);
      applySeo({
        title: HOME_TITLE,
        description: HOME_DESCRIPTION,
        keywords: HOME_KEYWORDS,
        canonical,
        ogType: 'website',
        ogTitle: HOME_TITLE,
        ogDescription: HOME_DESCRIPTION,
        ogUrl: canonical,
        twitterTitle: HOME_TITLE,
        twitterDescription: HOME_DESCRIPTION,
      });
      injectJsonLd('ld-website', buildWebSiteSchema());
      injectJsonLd('ld-organization', buildOrganizationSchema());
      removeJsonLd('ld-article');
      removeJsonLd('ld-breadcrumb');
      return;
    }

    if (segments.includes('article')) {
      const slug = segments[segments.length - 1];
      const article = articles[slug];
      if (article) {
        const canonical = buildUrl(path);
        const lang = segments[0];
        applySeo({
          title: article.title,
          description: article.excerpt,
          keywords: ARTICLE_KEYWORDS,
          canonical,
          ogType: 'article',
          ogTitle: article.title,
          ogDescription: article.excerpt,
          ogUrl: canonical,
          twitterTitle: article.title,
          twitterDescription: article.excerpt,
        });
        injectJsonLd('ld-article', buildArticleSchema(article, canonical));
        injectJsonLd(
          'ld-breadcrumb',
          buildBreadcrumbSchema([
            { name: 'Home', item: `${getSiteUrl()}/` },
            { name: 'Guides', item: `${getSiteUrl()}/${lang}` },
            { name: article.title, item: canonical },
          ])
        );
        removeJsonLd('ld-website');
        removeJsonLd('ld-organization');
      } else {
        applySeo({
          title: 'Article Not Found — AllChinaBuy Spreadsheet',
          description: 'The requested article could not be found.',
          keywords: ARTICLE_KEYWORDS,
          canonical: buildUrl(path),
          ogType: 'website',
          ogTitle: 'Article Not Found',
          ogDescription: 'The requested article could not be found.',
          ogUrl: buildUrl(path),
          twitterTitle: 'Article Not Found',
          twitterDescription: 'The requested article could not be found.',
          robots: 'noindex, nofollow',
        });
        clearArticleJsonLd();
      }
      return;
    }

    applySeo({
      title: 'Page Not Found — AllChinaBuy Spreadsheet',
      description: 'The page you are looking for could not be found.',
      keywords: ARTICLE_KEYWORDS,
      canonical: buildUrl(path),
      ogType: 'website',
      ogTitle: 'Page Not Found',
      ogDescription: 'The page you are looking for could not be found.',
      ogUrl: buildUrl(path),
      twitterTitle: 'Page Not Found',
      twitterDescription: 'The page you are looking for could not be found.',
      robots: 'noindex, nofollow',
    });
    clearArticleJsonLd();
  }, [location.pathname]);

  return null;
}